import { Enum } from "../enum";
import { Promise } from "../promise";
import { ArrayTypeRef } from "./arrays";
import { ClassRef, Constructor } from "./classes";
import { EnumRef } from "./enum";
import { InterfaceRef } from "./interfaces";
import type { Method } from "./methods";
import { Primitive, PrimitiveRef, PrimitiveRefFor } from "./primitives";
import { PromiseTypeRef } from "./promises";
import { RecordTypeRef } from "./records";
import type { Void, VoidRef } from "./void";


export namespace Type
{
    export function isType(type: TypeRef, value: any): boolean
    {
        const primitiveRef = PrimitiveRef.getFor(value);
        if (primitiveRef == type)
            return true;

        if (type == Date)
            return value instanceof Date;

        if (ClassRef.isClassRef(type))
            return value?.constructor == type;

        if (InterfaceRef.isInterfaceRef(type))
            return typeof value == "object";

        if (ArrayTypeRef.isArrayTypeRef(type))
            return Array.isArray(value);

        if (RecordTypeRef.isRecordTypeRef(type))
            return typeof value == "object";

        if (EnumRef.isEnumRef(type))
            return Enum.isEnum(type, value);

        return false;
    }

    const primitives: Record<string, PrimitiveValidator> =
    {
        [Boolean.name]: value => typeof value == "boolean",
        [Number.name]: value => typeof value == "number",
        [String.name]: value => typeof value == "string",
    };

    type PrimitiveValidator = (value: any) => boolean;
}


export type Any<T> = void | {} extends T ? T : never;

export type Combine<T, U> =
    T extends Empty
    ? (U extends Empty ? Empty : U)
    : (U extends Empty ? T : T & U);


export type Empty = { [k: keyof any]: never; };


export type FieldOf<T> = { [K in keyof T]: (T[K] extends Method ? never : K extends string ? K : never) }[keyof T];


export type FieldTypes = Record<string, TypeRef>;


export type FieldTypesFor<T> = { [F in FieldOf<T>]: TypeRefFor<T[F]> };


export type MemberOf<T> = Extract<keyof T, string>;


export type MethodOf<T, M extends Method = Method> = { [K in keyof T]: K extends string ? (T[K] extends M ? K : never) : never }[keyof T];


export type Methods<T, M extends Method = Method> = { [K in MethodOf<T, M>]: T[K]; };


export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;


export type OptionalFieldsOf<T extends object> = Exclude<{
    [K in keyof T]: T extends Record<K, T[K]>
    ? never
    : K
}[keyof T], undefined>;

export type RequiredKeysOf<T> = {
    [K in keyof T]-?: {} extends Pick<T, K> ? never : K
}[keyof T];


export type Parts<T> =
    T extends Primitive ? T :
    T extends Function ? never :
    T extends object ? { [K in FieldOf<T>]?: Parts<T[K]>; } :
    never;


export type TypeFor<T> =
    T extends typeof Boolean ? boolean :
    T extends typeof Number ? number :
    T extends typeof String ? string :
    T extends ArrayTypeRef<infer I> ? I[] :
    T extends RecordTypeRef<infer I> ? Record<string, I> :
    T extends PromiseTypeRef<infer R> ? Promise<R> :
    T extends Constructor<infer C> ? C :
    T extends InterfaceRef<infer I> ? I :
    T extends VoidRef ? void :
    T extends EnumRef<infer E> ? E :
    never;


export type TypeRef = PrimitiveRef | EnumRef | ArrayTypeRef | RecordTypeRef | PromiseTypeRef | InterfaceRef | ClassRef | VoidRef;


export type TypeRefFor<T> =
    T extends Any<T> ? TypeRef :
    T extends void ? VoidRef :
    T extends Enum<T> ? EnumRef :
    // Node extends T ? typeof Node :
    T extends Primitive ? PrimitiveRefFor<T> :
    T extends Date ? typeof Date :
    T extends (infer I)[] ? ArrayTypeRef<I> :
    // T extends Record<string, (infer I)> ? RecordTypeRef<I> :
    T extends Promise<infer R> ? PromiseTypeRef<R> :
    T extends object ? InterfaceRef<T> :
    never;


export type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };


export { Void };

